import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SignInRegister = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get('reset');
  const [activeTab, setActiveTab] = useState(resetToken ? 'reset' : 'signin');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const [captchaNum1, setCaptchaNum1] = useState(0);
  const [captchaNum2, setCaptchaNum2] = useState(0);
  const [captchaAnswer, setCaptchaAnswer] = useState('');

  const generateCaptcha = () => {
    setCaptchaNum1(Math.floor(Math.random() * 10) + 1);
    setCaptchaNum2(Math.floor(Math.random() * 10) + 1);
    setCaptchaAnswer('');
  };

  useEffect(() => {
    if (activeTab === 'register') generateCaptcha();
  }, [activeTab]);

  const [signInData, setSignInData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({
    name: '', email: '', password: '', greeting: '', academic_title: '',
    primary_affiliation: '', primary_position: '', secondary_affiliation: '',
    secondary_position: '', tertiary_affiliation: '', tertiary_position: '',
  });
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetData, setResetData] = useState({ new_password: '', confirm_password: '' });

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    const result = await login(signInData.email, signInData.password);
    if (result.success) navigate('/');
    else setError(result.error);
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (parseInt(captchaAnswer) !== captchaNum1 + captchaNum2) {
      setError('Incorrect captcha answer. Please try again.');
      generateCaptcha();
      return;
    }
    setLoading(true);
    const result = await register(registerData);
    if (result.success) navigate('/');
    else { setError(result.error); generateCaptcha(); }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setSuccess('If that email exists in our system, a password reset link has been sent. Please check your inbox.');
    } catch (err) {
      setError('Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (resetData.new_password !== resetData.confirm_password) {
      setError('Passwords do not match.');
      return;
    }
    if (resetData.new_password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token: resetToken, new_password: resetData.new_password });
      setSuccess('Password reset successfully! You can now sign in.');
      setTimeout(() => setActiveTab('signin'), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired reset token.');
    }
    setLoading(false);
  };

  return (
    <div data-testid="signin-register-page">
      <div className="auth-form" style={{ maxWidth: activeTab === 'register' ? '650px' : '450px' }}>
        <div className="auth-tabs" data-testid="auth-tabs">
          {resetToken ? (
            <button className="auth-tab active">{t('auth.resetPassword')}</button>
          ) : (
            <>
              <button className={`auth-tab ${activeTab === 'signin' ? 'active' : ''}`}
                onClick={() => { setActiveTab('signin'); setError(''); setSuccess(''); }} data-testid="signin-tab">{t('auth.signIn')}</button>
              <button className={`auth-tab ${activeTab === 'register' ? 'active' : ''}`}
                onClick={() => { setActiveTab('register'); setError(''); setSuccess(''); }} data-testid="register-tab">{t('auth.register')}</button>
            </>
          )}
        </div>

        {error && <div className="error-message" data-testid="auth-error">{error}</div>}
        {success && <div className="success-message" data-testid="auth-success">{success}</div>}

        {activeTab === 'reset' && resetToken && (
          <form onSubmit={handleResetPassword} data-testid="reset-password-form">
            <h2 style={{ textAlign: 'center', marginBottom: '1rem' }}>{t('auth.setNewPassword')}</h2>
            <p style={{ color: 'var(--muted)', fontSize: '0.9rem', marginBottom: '1.5rem', textAlign: 'center' }}>{t('auth.resetDesc')}</p>
            <div className="form-group">
              <label>{t('auth.newPassword')}</label>
              <input type="password" value={resetData.new_password} onChange={e => setResetData(p => ({...p, new_password: e.target.value}))} required minLength="6" data-testid="new-password-input" />
            </div>
            <div className="form-group">
              <label>{t('auth.confirmPassword')}</label>
              <input type="password" value={resetData.confirm_password} onChange={e => setResetData(p => ({...p, confirm_password: e.target.value}))} required data-testid="confirm-password-input" />
            </div>
            <button type="submit" className="btn" style={{ width: '100%' }} disabled={loading} data-testid="reset-submit-btn">{loading ? t('auth.resetting') : t('auth.resetBtn')}</button>
          </form>
        )}

        {activeTab === 'signin' && !resetToken && (
          <form onSubmit={handleSignIn} data-testid="signin-form">
            <h2 style={{ textAlign: 'center', marginBottom: '1.5rem' }}>{t('auth.welcomeBack')}</h2>
            <p style={{ color: 'var(--muted)', fontSize: '0.9rem', marginBottom: '1.5rem', textAlign: 'center' }}>{t('auth.signInDesc')}</p>
            <div className="form-group">
              <label htmlFor="signin-email">{t('auth.email')}</label>
              <input id="signin-email" name="email" type="email" value={signInData.email} onChange={e => setSignInData(p => ({...p, email: e.target.value}))} required data-testid="signin-email-input" placeholder="your.email@example.com" />
            </div>
            <div className="form-group">
              <label htmlFor="signin-password">{t('auth.password')}</label>
              <input id="signin-password" name="password" type="password" value={signInData.password} onChange={e => setSignInData(p => ({...p, password: e.target.value}))} required data-testid="signin-password-input" />
            </div>
            <button type="submit" className="btn" style={{ width: '100%' }} disabled={loading} data-testid="signin-submit-button">{loading ? t('auth.signingIn') : t('auth.signIn')}</button>
            <p style={{ textAlign: 'center', marginTop: '1rem' }}>
              <button type="button" className="link-btn" onClick={() => { setActiveTab('forgot'); setError(''); setSuccess(''); }} data-testid="forgot-password-link">{t('auth.forgotPassword')}</button>
            </p>
          </form>
        )}

        {activeTab === 'forgot' && (
          <form onSubmit={handleForgotPassword} data-testid="forgot-password-form">
            <h2 style={{ textAlign: 'center', marginBottom: '1rem' }}>{t('auth.resetPassword')}</h2>
            <p style={{ color: 'var(--muted)', fontSize: '0.9rem', marginBottom: '1.5rem', textAlign: 'center' }}>{t('auth.resetDesc')}</p>
            <div className="form-group">
              <label>{t('auth.email')}</label>
              <input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} required data-testid="forgot-email-input" placeholder="your.email@example.com" />
            </div>
            <button type="submit" className="btn" style={{ width: '100%' }} disabled={loading} data-testid="forgot-submit-btn">{loading ? t('auth.sending') : t('auth.sendResetLink')}</button>
            <p style={{ textAlign: 'center', marginTop: '1rem' }}>
              <button type="button" className="link-btn" onClick={() => { setActiveTab('signin'); setError(''); setSuccess(''); }} data-testid="back-to-signin">{t('auth.backToSignIn')}</button>
            </p>
          </form>
        )}

        {activeTab === 'register' && !resetToken && (
          <form onSubmit={handleRegister} data-testid="register-form">
            <h2 style={{ textAlign: 'center', marginBottom: '0.5rem' }}>{t('auth.joinAfhos')}</h2>
            <p style={{ color: 'var(--muted)', fontSize: '0.9rem', marginBottom: '1.5rem', textAlign: 'center' }}>{t('auth.registerDesc')}</p>
            <div className="form-section">
              <h3 style={{ color: 'var(--brand)', marginBottom: '1rem' }}>{t('auth.personalInfo')}</h3>
              <div className="form-row">
                <div className="form-group" style={{ flex: '0 0 30%' }}>
                  <label>{t('auth.greeting')}</label>
                  <select name="greeting" value={registerData.greeting} onChange={e => setRegisterData(p => ({...p, greeting: e.target.value}))} data-testid="register-greeting-select">
                    <option value="">Select...</option>
                    <option value="Mr">Mr</option><option value="Ms">Ms</option><option value="Mrs">Mrs</option><option value="Non disclosed">Non disclosed</option>
                  </select>
                </div>
                <div className="form-group" style={{ flex: '0 0 65%' }}>
                  <label>{t('auth.academicTitle')}</label>
                  <select name="academic_title" value={registerData.academic_title} onChange={e => setRegisterData(p => ({...p, academic_title: e.target.value}))} data-testid="register-academic-title-select">
                    <option value="">Select...</option>
                    <option value="Dr">Dr</option><option value="Prof">Prof</option><option value="Prof Dr">Prof Dr</option><option value="Non disclosed">Non disclosed</option><option value="Others">Others</option>
                  </select>
                </div>
              </div>
              <div className="form-group"><label>{t('auth.fullName')} *</label><input value={registerData.name} onChange={e => setRegisterData(p => ({...p, name: e.target.value}))} required data-testid="register-name-input" /></div>
              <div className="form-group"><label>{t('auth.email')} *</label><input type="email" value={registerData.email} onChange={e => setRegisterData(p => ({...p, email: e.target.value}))} required data-testid="register-email-input" /></div>
              <div className="form-group"><label>{t('auth.password')} *</label><input type="password" value={registerData.password} onChange={e => setRegisterData(p => ({...p, password: e.target.value}))} required minLength="6" data-testid="register-password-input" /></div>
            </div>
            <div className="form-section">
              <h3 style={{ color: 'var(--brand)', marginBottom: '1rem' }}>{t('auth.primaryAffiliation')}</h3>
              <div className="form-group"><label>{t('auth.institution')}</label><input value={registerData.primary_affiliation} onChange={e => setRegisterData(p => ({...p, primary_affiliation: e.target.value}))} data-testid="register-primary-affiliation-input" /></div>
              <div className="form-group"><label>{t('auth.position')}</label><input value={registerData.primary_position} onChange={e => setRegisterData(p => ({...p, primary_position: e.target.value}))} data-testid="register-primary-position-input" /></div>
            </div>
            <div className="form-section">
              <h3 style={{ color: 'var(--brand)', marginBottom: '1rem' }}>{t('auth.secondaryAffiliation')}</h3>
              <div className="form-group"><label>{t('auth.institution')}</label><input value={registerData.secondary_affiliation} onChange={e => setRegisterData(p => ({...p, secondary_affiliation: e.target.value}))} data-testid="register-secondary-affiliation-input" /></div>
              <div className="form-group"><label>{t('auth.position')}</label><input value={registerData.secondary_position} onChange={e => setRegisterData(p => ({...p, secondary_position: e.target.value}))} data-testid="register-secondary-position-input" /></div>
            </div>
            <div className="form-section">
              <h3 style={{ color: 'var(--brand)', marginBottom: '1rem' }}>{t('auth.tertiaryAffiliation')}</h3>
              <div className="form-group"><label>{t('auth.institution')}</label><input value={registerData.tertiary_affiliation} onChange={e => setRegisterData(p => ({...p, tertiary_affiliation: e.target.value}))} data-testid="register-tertiary-affiliation-input" /></div>
              <div className="form-group"><label>{t('auth.position')}</label><input value={registerData.tertiary_position} onChange={e => setRegisterData(p => ({...p, tertiary_position: e.target.value}))} data-testid="register-tertiary-position-input" /></div>
            </div>
            <div className="form-section" style={{ borderBottom: 'none' }}>
              <h3 style={{ color: 'var(--brand)', marginBottom: '1rem' }}>{t('auth.securityVerification')}</h3>
              <div className="captcha-container">
                <label>{t('auth.captchaQuestion', { num1: captchaNum1, num2: captchaNum2 })} *</label>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <input type="number" value={captchaAnswer} onChange={e => setCaptchaAnswer(e.target.value)} required data-testid="captcha-input" style={{ maxWidth: '150px' }} />
                  <button type="button" onClick={generateCaptcha} className="btn btn-secondary btn-small" data-testid="captcha-refresh"><i className="fas fa-sync-alt"></i> {t('auth.captchaNew')}</button>
                </div>
              </div>
            </div>
            <button type="submit" className="btn" style={{ width: '100%', marginTop: '1rem' }} disabled={loading} data-testid="register-submit-button">{loading ? t('auth.creating') : t('auth.registerBtn')}</button>
            <p style={{ textAlign: 'center', marginTop: '1rem', color: 'var(--muted)', fontSize: '0.85rem' }}>{t('auth.pendingNote')}</p>
          </form>
        )}
      </div>
    </div>
  );
};

export default SignInRegister;
